"use client";

import { useEffect, useRef } from "react";

import {
  useAddToWishlistMutation,
  useGetWishlistQuery,
  useMergeGuestWishlistMutation,
} from "@/src/features/wishlist/wishlistApi";
import { useAppDispatch, useAppSelector } from "@/src/store/hooks";
import { clearGuestWishlist } from "@/src/store/slices/wishlistSlice";

export default function WishlistSyncProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isAuthInitialized = useAppSelector((state) => state.auth.isInitialized);
  const legacyVariantIds = useAppSelector((state) => state.wishlist.variantIds);
  const migrationRunningRef = useRef(false);
  const mergeStartedRef = useRef(false);
  const { data: wishlist } = useGetWishlistQuery(undefined, {
    skip: !isAuthInitialized,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [addToWishlist] = useAddToWishlistMutation();
  const [mergeGuestWishlist] = useMergeGuestWishlistMutation();

  // One-time compatibility migration for data saved by older releases.
  // New wishlist changes always go through the backend API.
  useEffect(() => {
    if (
      !isAuthInitialized ||
      !wishlist ||
      legacyVariantIds.length === 0 ||
      migrationRunningRef.current
    ) {
      return;
    }

    migrationRunningRef.current = true;
    const existingIds = new Set(wishlist.items.map((item) => item.variantId));
    const missingIds = Array.from(new Set(legacyVariantIds)).filter(
      (variantId) => !existingIds.has(variantId),
    );

    void (async () => {
      try {
        for (const variantId of missingIds) {
          await addToWishlist({ variantId }).unwrap();
        }
        dispatch(clearGuestWishlist());
      } catch {
        // Keep legacy IDs so a later page load can safely retry the migration.
      } finally {
        migrationRunningRef.current = false;
      }
    })();
  }, [addToWishlist, dispatch, isAuthInitialized, legacyVariantIds, wishlist]);

  // One server-owned merge runs when a guest becomes authenticated. The guest
  // cookie is retained by the API if any item fails, so nothing is lost.
  useEffect(() => {
    if (!isAuthInitialized || !isAuthenticated) {
      mergeStartedRef.current = false;
      return;
    }

    if (mergeStartedRef.current) return;
    mergeStartedRef.current = true;

    void mergeGuestWishlist()
      .unwrap()
      .catch(() => {
        mergeStartedRef.current = false;
      });
  }, [isAuthInitialized, isAuthenticated, mergeGuestWishlist]);

  return children;
}
